// The markers kin-chat emits to OPEN a tool modal — issue #1434, #1781.
//
// ── WHY THESE LIVE IN ONE MODULE ────────────────────────────────────────────
//
// They used to be four `const`s declared inside `sendMessage` in Index.tsx, and
// the persist a few lines below used only TWO of them:
//
//   const finalAssistantText = assistantSoFar
//     .split(TRACKER_TAG).join("")
//     .split(GARDEN_TAG).join("")          // ← GIFT_TAG and SUMMER_TAG never used
//     .replace(/\[LEARN\]…/gi, "")
//
// so `[OPEN_GIFT_TRACKER]` and `[OPEN_SUMMER_PLANNER]` went into messages.content
// verbatim. Nothing removed them at render either — ChatMessages.tsx's
// `cleanContent` chain had zero references to any of the four — so a reload
// showed the raw marker as literal text in her chat.
//
// It hid because the LIVE turn looks correct: upsertAssistant strips each tag
// from `displayText` as it fires the modal, so the bug only appears in a session
// that did not create the message. It reads as a glitch, and never reproduces
// where it was made.
//
// A declaration list and a use list that can drift is the whole mechanism. One
// exported array, iterated at both sites, is what stops it happening again.
//
// ── WHAT THESE ARE, AND WHAT THEY ARE NOT ───────────────────────────────────
//
// ACTION markers: each one FIRES something (Index.tsx's
// `setTimeout(() => setShowX(true), 500)`). They are never content, so both the
// persist and the render remove them — the same rule
// supabase/functions/_shared/stripModelTags.ts calls REPLAY_ACTION_TAGS for the
// SMS surface (#1423).
//
// ⚠️ [BUDGET_TRACKER_BUTTON] is NOT one of these and must not be added. It
// renders a button she taps (ChatMessages.tsx:1171); removing it would delete
// the button rather than a stray marker. It is CONTENT.

/**
 * Every marker whose only job is to open a modal.
 *
 * ⚠️ ORDER IS SIGNIFICANT, AND APPEND-ONLY. This comment previously said the
 * opposite, and it was wrong (found while adding the protein tag, #1781).
 * Index.tsx:1628 destructures this array POSITIONALLY:
 *
 *   const [TRACKER_TAG, GIFT_TAG, GARDEN_TAG, SUMMER_TAG, PROTEIN_TAG] = MODAL_TAGS;
 *
 * Insert a tag anywhere but the END and every name after it silently shifts —
 * put one at index 1 and GIFT_TAG becomes the new tag, so "open my gift
 * tracker" opens the wrong tool and vice versa. No error, no type failure. The
 * order here is pinned by modalTags.test.ts for exactly that reason.
 */
export const MODAL_TAGS = [
  "[OPEN_BUDGET_TRACKER]",
  "[OPEN_GIFT_TRACKER]",
  "[OPEN_GARDEN_PLANNER]",
  "[OPEN_SUMMER_PLANNER]",
  "[OPEN_PROTEIN_TRACKER]",
  // #1900 — APPENDED, per the warning above. Routines is the first of these
  // that also carries a payload: the reply ends with this marker AND a hidden
  // [ROUTINE_DRAFT] block the editor prefills from. The marker belongs here
  // because its only job is still to open the modal; the draft block is a
  // PAYLOAD tag and is handled by stripRoutineDraft, not by this list.
  "[OPEN_ROUTINES]",
] as const;

/**
 * Removes every modal marker from a message string.
 *
 * Plain `split`/`join` rather than a regex, matching what Index.tsx already did
 * for the two it handled — these are fixed literals with no attribute forms, so
 * a regex would add escaping risk for nothing.
 *
 * Deliberately does NOT trim: callers differ on what they want at the ends
 * (the persist path trims, the render path leaves surrounding text alone), and
 * a trim baked in here would silently change one of them.
 */
export function stripModalTags(text: string): string {
  let out = text;
  for (const tag of MODAL_TAGS) out = out.split(tag).join("");
  return out;
}
